import {Component} from '@theme/component';

/**
 * Keeps the Tea Ducks product form compatible with Shopify and Appstle without
 * reproducing Appstle's pricing, labels, cadence, or selection interface.
 * @extends {Component<{variantInput: HTMLInputElement | HTMLSelectElement,
 *   quantityInput: HTMLInputElement, variants: HTMLScriptElement, labels: HTMLScriptElement}>}
 */
class TeaDucksProduct extends Component {
  requiredRefs = ['variantInput', 'quantityInput', 'variants', 'labels'];
  selectedPlanId = '';
  inFlight = false;

  connectedCallback() { super.connectedCallback(); this.initialize(); }
  updatedCallback() { super.updatedCallback(); this.initialize(); }

  disconnectedCallback() {
    this.listeners?.abort();
    this.formObserver?.disconnect();
    this.widgetObserver?.disconnect();
    super.disconnectedCallback();
  }

  initialize() {
    this.listeners?.abort();
    this.formObserver?.disconnect();
    this.widgetObserver?.disconnect();
    this.listeners = new AbortController();
    this.form = this.querySelector('form');
    if (!(this.form instanceof HTMLFormElement)) return;

    try {
      this.variants = JSON.parse(this.refs.variants.textContent);
      this.labels = JSON.parse(this.refs.labels.textContent);
      if (!Array.isArray(this.variants)) return;
    } catch { return; }

    const options = {signal: this.listeners.signal};
    this.form.addEventListener('change', (event) => this.handleChange(event), options);
    this.refs.quantityInput.addEventListener('input', () => this.updateButton(), options);
    this.form.addEventListener('formdata', (event) => this.canonicalizeSellingPlan(event), options);
    this.form.addEventListener('submit', (event) => this.validateSubmission(event), {...options, capture: true});
    document.addEventListener('tea-ducks:cart-settled', () => {
      if (!this.inFlight) this.updateButton();
    }, options);

    document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', () => this.syncPlanFromWidget(), options);
    document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected', () => {
      this.selectedPlanId = '';
      this.updateButton();
    }, options);
    document.addEventListener('AppstleSubscription:SubscriptionWidget:sellingPlanChanged', (event) => {
      this.selectedPlanId = this.normalizePlanId(event.detail);
      this.updateButton();
    }, options);

    this.formObserver = new MutationObserver(() => this.observeAppstleWidget());
    this.formObserver.observe(this.form, {childList: true});
    this.observeAppstleWidget();

    this.updateVariant();
  }

  /** @param {Event} event */
  handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (target === this.refs.variantInput) {
      this.selectedPlanId = '';
      this.updateVariant();
      return;
    }
    if (this.appstleWidget()?.contains(target)) this.selectedPlanId = this.planIdFromWidgetFields();
    this.updateButton();
  }

  appstleWidget() {
    return this.querySelector('.appstle_sub_widget');
  }

  observeAppstleWidget() {
    const widget = this.appstleWidget();
    if (widget === this.observedWidget) return;
    this.widgetObserver?.disconnect();
    this.observedWidget = widget;
    if (!widget) return;
    this.widgetObserver = new MutationObserver(() => this.syncPlanFromWidget());
    this.widgetObserver.observe(widget, {childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'value', 'data-selected-selling-plan']});
    this.syncPlanFromWidget();
  }

  syncPlanFromWidget() {
    requestAnimationFrame(() => {
      this.selectedPlanId = this.planIdFromWidgetFields();
      this.updateButton();
    });
  }

  /** @param {unknown} value */
  normalizePlanId(value) {
    if (value && typeof value === 'object') value = value.sellingPlanId ?? value.id ?? value.value ?? '';
    const raw = String(value ?? '').trim();
    const normalized = raw.startsWith('gid://') ? raw.split('/').pop() : raw;
    return /^\d+$/.test(normalized) ? normalized : '';
  }

  planIdFromWidgetFields() {
    const widget = this.appstleWidget();
    if (!widget) return '';
    const fields = [
      widget.querySelector('input[name="selling_plan"]:checked'),
      widget.querySelector('select[name="selling_plan"]'),
      widget.querySelector('input[name="selling_plan"][type="hidden"]'),
    ];
    for (const field of fields) {
      if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
        const planId = this.normalizePlanId(field.value);
        if (planId) return planId;
      }
    }
    return this.normalizePlanId(widget.dataset.selectedSellingPlan);
  }

  variant() {
    return this.variants.find((item) => String(item.id) === this.refs.variantInput.value);
  }

  state() {
    const variant = this.variant();
    const quantity = Number(this.refs.quantityInput.value);
    const purchase = this.form.querySelector('input[name="td_purchase"]:checked');
    const selected = this.form.querySelector('[data-moonlight-plan][aria-pressed="true"]');
    const candidatePlanId = purchase
      ? purchase.value === 'subscription' ? String(selected?.dataset.moonlightPlan || '') : ''
      : this.planIdFromWidgetFields() || this.selectedPlanId;
    const planIds = Array.isArray(variant?.planIds) ? variant.planIds.map(String) : [];
    const planId = planIds.includes(candidatePlanId) ? candidatePlanId : '';
    const rule = variant?.quantityRule ?? {};
    const minimum = Number(rule.min ?? 1);
    const maximum = Math.min(Number(rule.max ?? 999), 999);
    const increment = Number(rule.increment ?? 1);
    const quantityValid = Number.isInteger(quantity)
      && quantity >= minimum
      && quantity <= maximum
      && (quantity - minimum) % increment === 0;
    const planValid = candidatePlanId === '' || planId !== '';
    const requiredPlanPresent = !variant?.requiresPlan || planId !== '';
    return {variant, quantityValid, planValid, requiredPlanPresent, planId};
  }

  updateVariant() {
    const variant = this.variant();
    const rule = variant?.quantityRule ?? {};
    this.refs.quantityInput.min = String(rule.min ?? 1);
    this.refs.quantityInput.max = String(Math.min(rule.max ?? 999, 999));
    this.refs.quantityInput.step = String(rule.increment ?? 1);
    const price = this.querySelector('[data-price]');
    if (price && Number.isSafeInteger(variant?.price)) {
      const currency = window.Shopify?.currency?.active || 'GBP';
      price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style: 'currency', currency}).format(variant.price / 100);
    }
    this.updateButton();
  }

  updateButton() {
    const state = this.state();
    const button = this.form.querySelector('button[name="add"]');
    const error = this.form.querySelector('[data-purchase-error]');
    if (!(button instanceof HTMLButtonElement)) return;
    const purchasesEnabled = this.dataset.purchases === 'true';
    const available = state.variant?.available === true;
    button.disabled = this.inFlight || !purchasesEnabled || !available || !state.quantityValid || !state.planValid || !state.requiredPlanPresent;
    button.textContent = !purchasesEnabled ? this.labels.disabled_button : available ? this.labels.add_to_bag : this.labels.unavailable_button;
    if (error) error.hidden = true;
  }

  /** @param {FormDataEvent} event */
  canonicalizeSellingPlan(event) {
    const state = this.state();
    event.formData.delete('selling_plan');
    if (state.planId) event.formData.set('selling_plan', state.planId);
  }

  /** @param {SubmitEvent} event */
  async validateSubmission(event) {
    const state = this.state();
    const purchasesEnabled = this.dataset.purchases === 'true';
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.inFlight) return;
    const error = this.form.querySelector('[data-purchase-error]');
    if (purchasesEnabled && state.variant?.available && state.quantityValid && state.planValid && state.requiredPlanPresent) {
      this.inFlight = true;
      try {
        await addTeaDucksFormToDrawer(this.form, this, {variant: state.variant, planId: state.planId});
      } catch (failure) {
        if (error) { error.textContent = failure.message; error.hidden = false; }
      } finally {
        this.inFlight = false;
        this.form.setAttribute('aria-busy', 'false');
        const message = error?.textContent;
        const showError = error && !error.hidden;
        this.updateButton();
        if (showError) { error.textContent = message; error.hidden = false; }
      }
      return;
    }
    if (!state.quantityValid) this.refs.quantityInput.reportValidity();
    if (error) {
      error.textContent = !state.planValid ? this.labels.invalid_plan : !state.requiredPlanPresent ? this.labels.required_plan : this.labels.invalid_quantity;
      error.hidden = false;
    }
  }
}

if (!customElements.get('td-product')) customElements.define('td-product', TeaDucksProduct);

export async function addTeaDucksFormToDrawer(form, source, options = {}) {
  if (form.getAttribute('aria-busy') === 'true' || !form.reportValidity()) return false;
  const button = form.querySelector('button[name="add"]');
  if (!button || button.disabled) return false;
  const labels = JSON.parse(document.getElementById('TdMasterLabels')?.textContent || '{}');
  let status = form.querySelector('[data-purchase-error]');
  if (!status) {
    status = document.createElement('p');
    status.dataset.purchaseError = '';
    status.setAttribute('role', 'alert');
    form.append(status);
  }
  status.hidden = true;
  const data = new FormData(form);
  const variant = options.variant;
  const id = String(data.get('id') || '');
  const quantity = Number(data.get('quantity') || 1);
  const plan = String(options.planId ?? data.get('selling_plan') ?? '');
  const plans = (variant?.planIds || variant?.plans?.map(item => item.id) || []).map(String);
  const rule = variant?.quantityRule || {min: variant?.min || 1, max: variant?.max, increment: variant?.increment || 1};
  const minimum = Number(rule.min || 1), increment = Number(rule.increment || 1);
  const maximum = Math.min(Number(rule.max || 999), 999);
  const allowed = source.closest('[data-purchases]')?.dataset.purchases !== 'false';
  try {
    if (!allowed || !variant?.available || String(variant.id) !== id) throw new Error(labels.unavailable);
    if (!Number.isInteger(quantity) || quantity < minimum || quantity > maximum || (quantity - minimum) % increment) throw new Error(labels.invalidQuantity);
    if (plan && !plans.includes(plan)) throw new Error(labels.invalidPlan);
    if (variant.requiresPlan && !plan) throw new Error(labels.requiredPlan);
    if (!window.TeaDucksCartSafety) throw new Error(labels.failed);
    const item = {id: Number(id), quantity};
    if (plan) item.selling_plan = Number(plan);
    const properties = {};
    for (const [name, value] of data) {
      const property = name.match(/^properties\[(.+)\]$/);
      if (property) properties[property[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    form.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const root = window.Shopify?.routes?.root || '/';
    await window.TeaDucksCartSafety.mutate(root + 'cart/add.js', {items: [item]});
    options.beforeOpen?.();
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (typeof drawer?.open === 'function') drawer.open();
    else window.location.assign(root + 'cart');
    return true;
  } catch (failure) {
    status.textContent = failure.message || labels.failed;
    status.hidden = false;
    return false;
  } finally {
    form.setAttribute('aria-busy', 'false');
    button.disabled = !allowed || variant?.available !== true;
  }
}

document.addEventListener('submit', event => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.closest('.td-accessory-track')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  let variant;
  try { variant = JSON.parse(form.querySelector('[data-accessory-variant]').textContent); }
  catch { return; }
  void addTeaDucksFormToDrawer(form, form, {variant, planId: ''});
}, {capture: true});
